import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import { FileText, Upload, Plus, Trash2, CheckCircle2, Printer } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicVGMPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState({
    shipper_name: "",
    shipper_address: "",
    weighing_company_name: "",
    weighing_company_address: "",
    booking_number: "",
    vessel_name: "",
    voyage: "",
    signatory_name: "",
    signatory_place: "",
    signatory_date: new Date().toISOString().slice(0, 10),
    remarks: "",
    containers: [{ container_number: "", seal_number: "", cargo_weight: "", tare_weight: "", total_vgm: "", method: "1" }],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/public/vgm/${token}`);
        setCtx(r.data);
        // Merge prefill
        const p = r.data.prefill || {};
        const saved = r.data.submission;
        const base = {
          ...form,
          shipper_name: p.shipper_name || "",
          shipper_address: p.shipper_address || "",
          booking_number: p.booking_number || "",
          vessel_name: p.vessel_name || "",
          voyage: p.voyage || "",
          containers: (p.containers && p.containers.length
            ? p.containers.map(c => ({ ...c, cargo_weight: "", tare_weight: "", total_vgm: "", method: "1" }))
            : form.containers),
        };
        setForm(saved ? { ...base, ...saved } : base);
      } catch (e) {
        setError(e?.response?.data?.detail || "Link is invalid or expired");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateRow = (i, k, v) => setForm(f => ({
    ...f,
    containers: f.containers.map((r, idx) => idx === i ? { ...r, [k]: v } : r),
  }));
  const addRow = () => setForm(f => ({
    ...f,
    containers: [...f.containers, { container_number: "", seal_number: "", cargo_weight: "", tare_weight: "", total_vgm: "", method: "1" }],
  }));
  const removeRow = (i) => setForm(f => ({
    ...f,
    containers: f.containers.filter((_, idx) => idx !== i),
  }));

  // Auto-compute Total VGM when method 2
  const computedTotal = (row) => {
    if (row.method !== "2") return row.total_vgm;
    const a = parseFloat(row.cargo_weight || 0);
    const b = parseFloat(row.tare_weight || 0);
    if (!isNaN(a) && !isNaN(b) && (a || b)) return (a + b).toFixed(0);
    return row.total_vgm;
  };

  const submit = async () => {
    if (!form.shipper_name.trim()) { toast.error("Shipper name is required"); return; }
    if (!form.signatory_name.trim()) { toast.error("Signatory name is required"); return; }
    if (!form.containers.some(r => r.container_number && r.total_vgm)) {
      toast.error("At least one container with Total VGM required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        containers: form.containers.map(r => ({ ...r, total_vgm: computedTotal(r) })),
      };
      await axios.post(`${API_URL}/public/vgm/${token}/submit`, payload);
      toast.success("Thank you — your VGM declaration has been submitted");
      const r = await axios.get(`${API_URL}/public/vgm/${token}`);
      setCtx(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API_URL}/public/vgm/${token}/pdf`, "_blank");
  };

  const uploadSigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API_URL}/public/vgm/${token}/upload-signed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Signed copy uploaded — thank you");
      const r = await axios.get(`${API_URL}/public/vgm/${token}`);
      setCtx(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-10">
      <div className="max-w-md text-center bg-white rounded-lg shadow p-8 border">
        <h1 className="text-xl font-semibold text-red-600 mb-2">Unable to open VGM form</h1>
        <p className="text-slate-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Toaster />
      <div className="max-w-5xl mx-auto bg-white rounded-xl shadow border">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-t-xl">
          <div className="flex items-center gap-3 mb-1">
            <FileText className="h-6 w-6" />
            <h1 className="text-2xl font-bold">Shipper VGM Declaration</h1>
          </div>
          <p className="text-blue-100 text-sm">
            Verified Gross Mass per SOLAS · PF <strong>{ctx?.pf_number}</strong> · Link valid until {ctx?.expires_at?.slice(0, 10)}
          </p>
          {ctx?.status === "submitted" && (
            <div className="mt-3 bg-green-600/80 rounded px-3 py-2 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Form submitted on {ctx?.submitted_at?.slice(0, 10)}. You can still print, sign and upload below.
            </div>
          )}
          {ctx?.status === "signed" && (
            <div className="mt-3 bg-emerald-700 rounded px-3 py-2 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Signed copy received — thank you.
            </div>
          )}
        </div>

        <div className="p-6 space-y-8">
          {/* Shipper details */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-3">1. Shipper &amp; Weighing Company</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Shipper Name *</Label>
                <Input data-testid="vgm-shipper-name" value={form.shipper_name} onChange={e => update("shipper_name", e.target.value)} />
              </div>
              <div>
                <Label>Shipper Address</Label>
                <Input value={form.shipper_address} onChange={e => update("shipper_address", e.target.value)} />
              </div>
              <div>
                <Label>Weighing Company (if 3rd party)</Label>
                <Input value={form.weighing_company_name} onChange={e => update("weighing_company_name", e.target.value)} />
              </div>
              <div>
                <Label>Weighing Company Address</Label>
                <Input value={form.weighing_company_address} onChange={e => update("weighing_company_address", e.target.value)} />
              </div>
              <div>
                <Label>Booking / BL Number</Label>
                <Input value={form.booking_number} onChange={e => update("booking_number", e.target.value)} />
              </div>
              <div>
                <Label>Vessel / Voyage</Label>
                <div className="flex gap-2">
                  <Input placeholder="Vessel" value={form.vessel_name} onChange={e => update("vessel_name", e.target.value)} />
                  <Input placeholder="Voyage" className="w-28" value={form.voyage} onChange={e => update("voyage", e.target.value)} />
                </div>
              </div>
            </div>
          </section>

          {/* Container rows */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900">2. Containers &amp; VGM</h2>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="vgm-add-row"><Plus className="h-3.5 w-3.5 mr-1" />Add container</Button>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Container No.</th>
                    <th className="px-3 py-2 font-medium">Seal</th>
                    <th className="px-3 py-2 font-medium">Cargo wt (kg)</th>
                    <th className="px-3 py-2 font-medium">Tare wt (kg)</th>
                    <th className="px-3 py-2 font-medium">Total VGM (kg)</th>
                    <th className="px-3 py-2 font-medium">Method</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {form.containers.map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-2 py-1"><Input className="h-8" value={row.container_number} onChange={e => updateRow(i, "container_number", e.target.value)} data-testid={`vgm-cn-${i}`} /></td>
                      <td className="px-2 py-1"><Input className="h-8" value={row.seal_number} onChange={e => updateRow(i, "seal_number", e.target.value)} /></td>
                      <td className="px-2 py-1"><Input className="h-8" inputMode="numeric" value={row.cargo_weight} onChange={e => updateRow(i, "cargo_weight", e.target.value)} /></td>
                      <td className="px-2 py-1"><Input className="h-8" inputMode="numeric" value={row.tare_weight} onChange={e => updateRow(i, "tare_weight", e.target.value)} /></td>
                      <td className="px-2 py-1">
                        <Input className="h-8 font-semibold" inputMode="numeric"
                          value={row.method === "2" ? computedTotal(row) : row.total_vgm}
                          disabled={row.method === "2"}
                          onChange={e => updateRow(i, "total_vgm", e.target.value)}
                          data-testid={`vgm-total-${i}`}
                          title={row.method === "2" ? "Auto-calculated (cargo + tare)" : "Weighed total"}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <select className="h-8 px-2 rounded border border-slate-200 text-sm"
                          value={row.method} onChange={e => updateRow(i, "method", e.target.value)}>
                          <option value="1">1 · Weighing</option>
                          <option value="2">2 · Calculation</option>
                        </select>
                      </td>
                      <td className="px-2 py-1 text-right">
                        {form.containers.length > 1 && (
                          <button className="text-red-500 hover:text-red-700" onClick={() => removeRow(i)} title="Remove row">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              <strong>Method 1</strong> — weigh the packed &amp; sealed container.
              &nbsp;<strong>Method 2</strong> — sum cargo + packaging + dunnage + container tare (auto-calculated).
            </p>
          </section>

          {/* Signatory */}
          <section>
            <h2 className="text-base font-semibold text-slate-900 mb-3">3. Declaration &amp; Signatory</h2>
            <p className="text-xs text-slate-600 mb-3">
              I certify that the VGM(s) above were determined in accordance with the SOLAS regulation. Missing/incorrect VGM may result in non-acceptance of the shipment by the vessel operator.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Signatory Name (CAPS) *</Label>
                <Input value={form.signatory_name} onChange={e => update("signatory_name", e.target.value.toUpperCase())} data-testid="vgm-signatory" />
              </div>
              <div>
                <Label>Place</Label>
                <Input value={form.signatory_place} onChange={e => update("signatory_place", e.target.value)} />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.signatory_date} onChange={e => update("signatory_date", e.target.value)} />
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="vgm-submit-btn">
              {saving ? "Submitting…" : (ctx?.status === "submitted" ? "Re-submit" : "Submit VGM")}
            </Button>
            {ctx?.status && ctx.status !== "sent" && (
              <Button variant="outline" onClick={downloadPdf} data-testid="vgm-pdf-btn"><Printer className="h-4 w-4 mr-1" /> Print / Download PDF</Button>
            )}
            <div>
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50">
                <Upload className="h-4 w-4" /> <span className="text-sm">{uploading ? "Uploading…" : "Upload signed copy"}</span>
                <input type="file" accept="application/pdf,image/*" hidden onChange={uploadSigned} data-testid="vgm-upload-signed" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
